import { FC, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { queryKeys } from 'utils/react-query';
import { useCurrentUserId, useLoggedInUserId } from 'modules/Auth/hooks';
import { apiGetUserFeaturedFlags } from 'api/users';
import { FeaturedFlagsContext, FeaturedFlagsUnion } from './FeaturedFlagsContext';
import { getFeaturedFlagsFromJSON } from './getFeaturedFlagsFromJSON';

export const FeaturedFlagsProvider: FC<{ children: React.ReactNode; enabledFeaturesMe: FeaturedFlagsUnion[] }> = ({
  children,
  enabledFeaturesMe,
}) => {
  useInitializeFeaturedFlagsMe(enabledFeaturesMe);

  const userOrOrgId = useCurrentUserId();
  const queryClient = useQueryClient();

  // Invalidate FeaturedFlags queries when user changes to ensure fresh data for new user
  useEffect(() => {
    if (userOrOrgId) {
      queryClient.invalidateQueries([queryKeys.FeaturedFlags]);
    }
  }, [userOrOrgId, queryClient]);

  const { data: enabledFeatures = [] } = useQuery([queryKeys.FeaturedFlags, { userOrOrgId }], {
    queryFn: async () => {
      const { config } = await apiGetUserFeaturedFlags({ userId: userOrOrgId });
      return getFeaturedFlagsFromJSON(config);
    },
    enabled: !!userOrOrgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 5, // 5 minutes
  });

  return <FeaturedFlagsContext.Provider value={{ enabledFeatures }}>{children}</FeaturedFlagsContext.Provider>;
};

function useInitializeFeaturedFlagsMe(enabledFeaturesMe: FeaturedFlagsUnion[]) {
  const queryClient = useQueryClient();
  const loggedInUserId = useLoggedInUserId();
  const isFeaturedFlagsCached = Boolean(queryClient.getQueryData([queryKeys.FeaturedFlags, { userOrOrgId: 'me' }]));

  if (!isFeaturedFlagsCached && loggedInUserId && enabledFeaturesMe.length) {
    queryClient.setQueryData([queryKeys.FeaturedFlags, { userOrOrgId: 'me' }], enabledFeaturesMe);
    queryClient.setQueryData([queryKeys.FeaturedFlags, { userOrOrgId: loggedInUserId }], enabledFeaturesMe);
  }
}
